import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import '../App.css';
import { annualPrice, MONTHLY_PRICE, usd } from '../pricing';
import FeaturesBento from '../components/FeaturesBento';

const faqs = [
  { q: 'How accurate is plant identification?', a: 'Gardeno uses advanced AI to identify plants with over 95% accuracy across 30,000+ species. For best results, take a clear photo in good lighting.' },
  { q: 'What\'s included in the free version?', a: 'Free users get 3 plant identifications and community access. Upgrade for unlimited identifications, plant diagnosis, the AI plant expert, care reminders, and more.' },
  { q: 'Can I cancel my subscription anytime?', a: 'Absolutely. Cancel through the App Store anytime. Features stay active until your billing period ends.' },
  { q: 'How does plant diagnosis work?', a: 'Take photos, answer a few questions about your plant\'s environment, and get a health score, diagnosis, and step-by-step treatment plan.' },
  { q: 'Is my data safe?', a: 'Yes. Your photos and data are encrypted. We never sell your personal information.' },
];

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
);

function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  // Live on the App Store. Full listing URL (gb storefront + slug); on
  // device Apple still hands other regions their own storefront.
  const APP_STORE_URL = 'https://apps.apple.com/gb/app/gardeno-plant-identifier/id6767298062';
  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(APP_STORE_URL, '_blank', 'noopener');
  };

  return (
    <>

      {/* Nav */}
      <nav className="nav">
        <div className="nav-inner">
          <Link to="/" className="nav-logo">
            <img src="/images/app-icon.png" alt="Gardeno" style={{ width: 32, height: 32, borderRadius: 8, marginRight: 8, verticalAlign: 'middle' }} />
            Gardeno
          </Link>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <a href="#" onClick={handleDownload} className="nav-cta">Download App</a>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileMenu(!mobileMenu)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mobile-nav"
          >
            <a href="#features" onClick={() => setMobileMenu(false)}>Features</a>
            <a href="#how-it-works" onClick={() => setMobileMenu(false)}>How It Works</a>
            <a href="#pricing" onClick={() => setMobileMenu(false)}>Pricing</a>
            <a href="#faq" onClick={() => setMobileMenu(false)}>FAQ</a>
            <a href="#" onClick={(e) => { handleDownload(e); setMobileMenu(false); }} className="nav-cta" style={{ textAlign: 'center', display: 'block', marginTop: 8 }}>Download App</a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero — Full Screen */}
      <section className="hero" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden', padding: '0 24px' }}>

        {/* Floating plants — continuously animated */}
        <div className="hero-plants">
          <motion.img
            src="/images/snake_plant.png" alt=""
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 0.3, y: [0, -15, 0] }}
            transition={{ opacity: { duration: 1, delay: 0.3 }, y: { duration: 6, repeat: Infinity, ease: 'easeInOut' } }}
            style={{ position: 'absolute', left: '2%', bottom: '5%', height: '42%', pointerEvents: 'none' }}
          />
          <motion.img
            src="/images/trailing_plant.png" alt=""
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 0.22, y: [0, 12, 0] }}
            transition={{ opacity: { duration: 1, delay: 0.5 }, y: { duration: 7, repeat: Infinity, ease: 'easeInOut' } }}
            style={{ position: 'absolute', right: '0%', top: '8%', height: '32%', pointerEvents: 'none' }}
          />
          <motion.img
            src="/images/lucky_bamboo.png" alt=""
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 0.2, y: [0, -10, 0], rotate: [0, 2, 0] }}
            transition={{ opacity: { duration: 1, delay: 0.7 }, y: { duration: 8, repeat: Infinity, ease: 'easeInOut' }, rotate: { duration: 8, repeat: Infinity, ease: 'easeInOut' } }}
            style={{ position: 'absolute', right: '6%', bottom: '10%', height: '38%', pointerEvents: 'none' }}
          />
          <motion.img
            src="/images/bush_plant.png" alt=""
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 0.2, y: [0, 10, 0] }}
            transition={{ opacity: { duration: 1, delay: 0.9 }, y: { duration: 5, repeat: Infinity, ease: 'easeInOut' } }}
            style={{ position: 'absolute', left: '8%', top: '12%', height: '22%', pointerEvents: 'none' }}
          />
          <motion.img
            src="/images/tall_cactus.png" alt=""
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 0.18, y: [0, -12, 0], rotate: [0, -1.5, 0] }}
            transition={{ opacity: { duration: 1, delay: 1.1 }, y: { duration: 9, repeat: Infinity, ease: 'easeInOut' }, rotate: { duration: 9, repeat: Infinity, ease: 'easeInOut' } }}
            style={{ position: 'absolute', left: '18%', bottom: '15%', height: '30%', pointerEvents: 'none' }}
          />
          <motion.img
            src="/images/cacti_flowers.png" alt=""
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 0.18, y: [0, 8, 0], rotate: [0, 1, 0] }}
            transition={{ opacity: { duration: 1, delay: 1.3 }, y: { duration: 7.5, repeat: Infinity, ease: 'easeInOut' }, rotate: { duration: 7.5, repeat: Infinity, ease: 'easeInOut' } }}
            style={{ position: 'absolute', right: '15%', top: '18%', height: '20%', pointerEvents: 'none' }}
          />
        </div>

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src="/images/app-icon.png" alt="Gardeno" style={{ width: 80, height: 80, borderRadius: 20, marginBottom: 24, boxShadow: '0 8px 30px rgba(27,67,50,0.15)' }} />
          </motion.div>

          <motion.p
            className="section-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Plant Identifier & Care
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            style={{ fontSize: 'clamp(40px, 7vw, 64px)', fontWeight: 800, color: 'var(--primary)', lineHeight: 1.1, maxWidth: 700, margin: '0 auto 24px' }}
          >
            Never Kill Another Plant
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ fontSize: 18, color: 'var(--text-secondary)', maxWidth: 520, margin: '0 auto 36px', lineHeight: 1.7 }}
          >
            Your personal plant expert, right in your pocket. Identify any plant in seconds, get care tips that actually work, and watch your garden thrive.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <a href="#" onClick={handleDownload} className="hero-cta"><AppleIcon /> Download for iOS</a>
            <span className="hero-badge">Free to try. Your first month is on us.</span>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)' }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            style={{ width: 24, height: 40, borderRadius: 12, border: '2px solid var(--text-tertiary)', display: 'flex', justifyContent: 'center', paddingTop: 8 }}
          >
            <div style={{ width: 3, height: 8, borderRadius: 2, background: 'var(--text-tertiary)' }} />
          </motion.div>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features">
        <div className="container">
          <p className="section-label">Features</p>
          <h2 className="section-title">Everything Your Plants Need</h2>
          <p className="section-subtitle">From identification to daily care, Gardeno is your complete plant companion.</p>
          <FeaturesBento />
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works">
        <div className="container section-center">
          <p className="section-label">How It Works</p>
          <h2 className="section-title">Three Steps to Happy Plants</h2>
          <p className="section-subtitle">It's as simple as taking a photo.</p>
          <div className="steps">
            {[
              { n: '1', t: 'Snap a Photo', d: 'Point your camera at any plant. Gardeno identifies it instantly with details and care info.' },
              { n: '2', t: 'Get Personalized Care', d: 'Receive watering schedules, sunlight tips, and care reminders tailored to your plant.' },
              { n: '3', t: 'Watch It Thrive', d: 'Track your plant\'s journey, diagnose problems early, and watch your garden grow.' },
            ].map((s, i) => (
              <motion.div className="step" key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}>
                <div className="step-number">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing">
        <div className="container section-center">
          <p className="section-label">Pricing</p>
          <h2 className="section-title">Start Free, Grow With Us</h2>
          <p className="section-subtitle">3 free scans to start. Upgrade for unlimited access.</p>
          <div className="pricing-cards">
            <motion.div className="pricing-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <h3>Free</h3>
              <div className="pricing-price">$0<span>/forever</span></div>
              <div className="pricing-period">No credit card needed</div>
              <ul className="pricing-features"><li>3 plant identifications</li><li>Browse scan history</li><li>Community access</li></ul>
              <a href="#" onClick={handleDownload} className="pricing-cta outline">Get Started</a>
            </motion.div>
            <motion.div className="pricing-card popular" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <div className="pricing-badge">Most Popular</div>
              <h3>Annual</h3>
              <div className="pricing-price">{usd(annualPrice())}<span>/year</span></div>
              <div className="pricing-period">Just {usd(annualPrice() / 12)}/month. First month free.</div>
              <ul className="pricing-features"><li>Unlimited plant scans</li><li>Plant diagnosis & treatment</li><li>Ask Gardeno (AI chat)</li><li>My Garden & care reminders</li><li>Recovery tracking</li></ul>
              <a href="#" onClick={handleDownload} className="pricing-cta primary">Start Free Trial</a>
            </motion.div>
            <motion.div className="pricing-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
              <h3>Monthly</h3>
              <div className="pricing-price">{usd(MONTHLY_PRICE)}<span>/month</span></div>
              <div className="pricing-period">First month free. Cancel anytime.</div>
              <ul className="pricing-features"><li>Everything in Annual</li><li>Flexible monthly billing</li><li>No long commitment</li></ul>
              <a href="#" onClick={handleDownload} className="pricing-cta outline">Subscribe</a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq">
        <div className="container section-center">
          <p className="section-label">FAQ</p>
          <h2 className="section-title">Common Questions</h2>
          <br />
          <div className="faq-list">
            {faqs.map((f, i) => (
              <div className={`faq-item ${openFaq === i ? 'open' : ''}`} key={i}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {f.q}
                  <span className="icon">+</span>
                </button>
                <div className="faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2>Your Plants Deserve the Best</h2>
          <p>Join thousands of plant parents growing with confidence.</p>
          <a href="#" onClick={handleDownload}><AppleIcon /> Download for iOS</a>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <h3><img src="/images/app-icon.png" alt="" style={{ width: 28, height: 28, borderRadius: 6, marginRight: 8, verticalAlign: 'middle' }} />Gardeno</h3>
            <p>Your personal plant expert. Identify, diagnose, and care for your plants with confidence.</p>
          </div>
          <div className="footer-col">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="footer-col">
            <h4>Legal</h4>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <Link to="/support">Support &amp; FAQ</Link>
            <a href="mailto:support@gardenoai.com">support@gardenoai.com</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>&copy; 2026 Gardeno. All rights reserved.</span>
          <span className="footer-company">USTACK GLOBAL (SMC-PRIVATE) LIMITED</span>
        </div>
      </footer>
    </>
  );
}

export default Home;
